import { useState } from "react";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { getStoreCurrencySymbol } from "../../../utils/currency";
import {
  UsersIcon,
  UserPlusIcon,
  CurrencyRupeeIcon,
  StarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  UserCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  CakeIcon,
  PencilSquareIcon,
  ChatBubbleLeftEllipsisIcon,
} from "@heroicons/react/24/outline";
import "./customers.css";

// ─── Mock Data ───────────────────────────────────────────────────────────────
const MOCK_CUSTOMERS = [
  {
    id: "CUST-001",
    name: "Rajesh Kumar",
    tier: "VIP",
    phone: "+91 8756785634",
    email: "rajesh.k@gmail.com",
    orders: 127,
    spent: 85231,
    points: 5432,
    avgOrderValue: 1250,
    lastVisit: "2 days ago",
    since: "Jan 15, 2023",
    birthday: "March 15",
    dietary: ["Non-Vegetarian"],
    favorites: ["Butter Chicken", "Garlic Naan", "Paneer Tikka"],
    notes: "Prefers table by the window. Always orders extra gravy.",
    recentOrders: [
      { id: "ORD-1234", date: "Feb 4, 2026", table: "Table 12", items: 3, amount: 1250, type: "Dine-In" },
      { id: "ORD-1235", date: "Feb 4, 2026", table: "Table 12", items: 3, amount: 890, type: "Takeaway" },
      { id: "ORD-1236", date: "Feb 4, 2026", table: "Table 12", items: 3, amount: 1250, type: "Delivery" },
    ],
  },
  {
    id: "CUST-002",
    name: "Priya Sharma",
    tier: "Gold",
    phone: "+91 9876543210",
    email: "priya.s@gmail.com",
    orders: 89,
    spent: 62000,
    points: 3210,
    avgOrderValue: 697,
    lastVisit: "5 days ago",
    since: "Mar 10, 2023",
    birthday: "June 22",
    dietary: ["Vegetarian"],
    favorites: ["Paneer Butter Masala", "Dal Makhani"],
    notes: "",
    recentOrders: [
      { id: "ORD-2001", date: "Mar 6, 2026", table: "Table 5", items: 2, amount: 740, type: "Dine-In" },
      { id: "ORD-2002", date: "Mar 1, 2026", table: "Table 5", items: 1, amount: 320, type: "Takeaway" },
    ],
  },
  {
    id: "CUST-003",
    name: "Arjun Mehta",
    tier: "Silver",
    phone: "+91 9123456789",
    email: "arjun.m@gmail.com",
    orders: 45,
    spent: 31500,
    points: 1560,
    avgOrderValue: 700,
    lastVisit: "1 week ago",
    since: "Jul 5, 2023",
    birthday: "Dec 3",
    dietary: ["Vegan"],
    favorites: ["Veg Biryani"],
    notes: "",
    recentOrders: [
      { id: "ORD-3001", date: "Mar 4, 2026", table: "Table 8", items: 2, amount: 560, type: "Delivery" },
    ],
  },
  {
    id: "CUST-004",
    name: "Sneha Patel",
    tier: "Bronze",
    phone: "+91 8801234567",
    email: "sneha.p@gmail.com",
    orders: 12,
    spent: 8400,
    points: 320,
    avgOrderValue: 700,
    lastVisit: "2 weeks ago",
    since: "Oct 20, 2023",
    birthday: "Aug 18",
    dietary: ["Non-Vegetarian"],
    favorites: ["Chicken Biryani", "Kebab"],
    notes: "",
    recentOrders: [
      { id: "ORD-4001", date: "Feb 25, 2026", table: "Table 3", items: 2, amount: 980, type: "Dine-In" },
    ],
  },
  {
    id: "CUST-005",
    name: "Vikram Singh",
    tier: "VIP",
    phone: "+91 9988776655",
    email: "vikram.s@gmail.com",
    orders: 203,
    spent: 146000,
    points: 14560,
    avgOrderValue: 719,
    lastVisit: "Yesterday",
    since: "Dec 1, 2022",
    birthday: "Feb 28",
    dietary: ["Non-Vegetarian"],
    favorites: ["Mutton Rogan Josh", "Biryani", "Seekh Kebab"],
    notes: "Prefers window seating",
    recentOrders: [
      { id: "ORD-5001", date: "Mar 10, 2026", table: "Table 1", items: 4, amount: 2100, type: "Dine-In" },
      { id: "ORD-5002", date: "Mar 9, 2026", table: "Table 1", items: 3, amount: 1450, type: "Dine-In" },
    ],
  },
];

const FILTER_TABS = ["All", "VIP", "Gold", "Active", "New"];

const SUMMARY_STATS = [
  {
    label: "Total Customer",
    value: "343",
    icon: UsersIcon,
    iconClass: "blue",
  },
  {
    label: "New Customer",
    value: "60",
    icon: UserPlusIcon,
    iconClass: "green",
  },
  {
    label: "Total Revenue",
    value: `${getStoreCurrencySymbol()}85,231`,
    icon: CurrencyRupeeIcon,
    iconClass: "purple",
  },
  {
    label: "Loyalty Points",
    value: "10 pts",
    icon: StarIcon,
    iconClass: "yellow",
  },
];

// ─── Tier Badge ───────────────────────────────────────────────────────────────
function TierBadge({ tier }) {
  return <span className={`tier-badge ${tier}`}>{tier}</span>;
}

// ─── Customer Card (list item) ────────────────────────────────────────────────
function CustomerCard({ customer, isSelected, onClick }) {
  return (
    <div
      className={`customer-card`}
      onClick={() => onClick(customer)}
    >
      <div className="customer-card-top">
        <div className="customer-avatar">
          <UserCircleIcon />
        </div>
        <div className="customer-name-block">
          <div className="customer-name-row">
            <span className="customer-name">{customer.name}</span>
            <TierBadge tier={customer.tier} />
          </div>
          <div className="customer-contact">
            {customer.phone} &bull; {customer.email}
          </div>
        </div>
      </div>

      <div className="customer-stats-row">
        <span>
          Orders: <strong>{customer.orders}</strong>
        </span>
        <span>
          Spent: <strong>{getStoreCurrencySymbol()}{customer.spent.toLocaleString("en-IN")}</strong>
        </span>
        <span>
          Points: <strong>{customer.points.toLocaleString("en-IN")}</strong>
        </span>
      </div>
      <div className="customer-last-visit">Last Visit: {customer.lastVisit}</div>
    </div>
  );
}

// ─── Right Panel: Empty State ─────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="customers-empty-state">
      <UsersIcon />
      <p>Select a customer to view details</p>
    </div>
  );
}

const ORDER_TYPE_CLASS = {
  "Dine-In": "order-type-dinein",
  Takeaway: "order-type-takeaway",
  Delivery: "order-type-delivery",
};

// ─── Right Panel: Customer Detail ─────────────────────────────────────────────
function CustomerDetail({ customer }) {
  return (
    <div className="customer-detail">
      {/* Profile Header */}
      <div className="detail-card">
        <div className="detail-profile-header">
          <div className="detail-avatar">
            <UserCircleIcon />
          </div>
          <div className="detail-name-block">
            <p className="detail-name">{customer.name}</p>
            <p className="detail-id">{customer.id}</p>
          </div>
          <button className="detail-edit-btn" title="Edit customer">
            <PencilSquareIcon />
          </button>
        </div>
        <div className="detail-tier-row">
          <TierBadge tier={customer.tier} />
          <span className="detail-since">Since {customer.since}</span>
        </div>
      </div>

      {/* Contact Information */}
      <div className="detail-section">
        <p className="section-title">Contact Information</p>
        <div className="contact-row">
          <div className="contact-icon phone-icon">
            <PhoneIcon />
          </div>
          <div className="contact-info">
            <span className="contact-label">Phone</span>
            <span className="contact-value">{customer.phone}</span>
          </div>
          <button className="contact-chat-btn" title="Send message">
            <ChatBubbleLeftEllipsisIcon />
          </button>
        </div>
        <div className="contact-row">
          <div className="contact-icon email-icon">
            <EnvelopeIcon />
          </div>
          <div className="contact-info">
            <span className="contact-label">Email</span>
            <span className="contact-value">{customer.email}</span>
          </div>
          <button className="contact-chat-btn" title="Send message">
            <ChatBubbleLeftEllipsisIcon />
          </button>
        </div>
      </div>

      {/* Special Dates */}
      <div className="detail-section">
        <p className="section-title">Special Dates</p>
        <div className="special-date-row">
          <div className="date-icon">
            <CakeIcon />
          </div>
          <div>
            <div className="date-label">Birthday</div>
            <div className="date-value">{customer.birthday}</div>
          </div>
        </div>
      </div>

      {/* Preferences & Dietary Info */}
      <div className="detail-section">
        <p className="section-title">Preferences &amp; Dietary Info</p>
        {customer.dietary && customer.dietary.length > 0 && (
          <>
            <div className="pref-subtitle">Dietary Preferences</div>
            <div className="pref-tags">
              {customer.dietary.map((d) => (
                <span
                  key={d}
                  className={`pref-tag dietary-${d.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {d}
                </span>
              ))}
            </div>
          </>
        )}
        {customer.favorites && customer.favorites.length > 0 && (
          <>
            <div className="pref-subtitle">Favorite Items</div>
            <div className="favorites-list">
              {customer.favorites.map((item) => (
                <div key={item} className="favorite-item">
                  {item}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Notes */}
      {customer.notes ? (
        <div className="detail-section">
          <p className="section-title">Notes</p>
          <div className="notes-alert">
            <span className="notes-alert-icon">&#9432;</span>
            <span className="notes-alert-text">{customer.notes}</span>
          </div>
        </div>
      ) : null}

      {/* Statistics */}
      <div className="detail-section">
        <p className="section-title">Statistics</p>
        <div className="stats-kv-list">
          <div className="stats-kv-row">
            <span className="stats-kv-label">Average Order Value</span>
            <span className="stats-kv-value">
              {getStoreCurrencySymbol()}{customer.avgOrderValue?.toLocaleString("en-IN")}.00
            </span>
          </div>
          <div className="stats-kv-row">
            <span className="stats-kv-label">Last Visit</span>
            <span className="stats-kv-value">{customer.lastVisit}</span>
          </div>
          <div className="stats-kv-row">
            <span className="stats-kv-label">Customer Since</span>
            <span className="stats-kv-value">{customer.since}</span>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      {customer.recentOrders && customer.recentOrders.length > 0 && (
        <div className="detail-section">
          <p className="section-title">Recent Orders</p>
          <div className="recent-orders-list">
            {customer.recentOrders.map((order) => (
              <div key={order.id} className="recent-order-card">
                <div className="recent-order-top">
                  <span className="recent-order-id">{order.id}</span>
                  <span className={`order-type-badge ${ORDER_TYPE_CLASS[order.type]}`}>
                    {order.type}
                  </span>
                </div>
                <div className="recent-order-meta">
                  <span>{order.date}</span>
                  <span className="recent-order-dot">•</span>
                  <span>{order.table}</span>
                </div>
                <div className="recent-order-bottom">
                  <span className="recent-order-items">{order.items} items</span>
                  <span className="recent-order-amount">
                    {getStoreCurrencySymbol()}{order.amount.toLocaleString("en-IN")}.00
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="detail-actions">
        <button className="btn-create-order">Create New Order +</button>
        <button className="btn-add-points">Add Points +</button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CustomersPage() {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  // Filter customers based on search text and active tab
  const filteredCustomers = MOCK_CUSTOMERS.filter((c) => {
    const matchesSearch =
      searchText.trim() === "" ||
      c.name.toLowerCase().includes(searchText.toLowerCase()) ||
      c.phone.includes(searchText) ||
      c.email.toLowerCase().includes(searchText.toLowerCase());

    const matchesTab =
      activeTab === "All" ||
      activeTab === "Active" ||
      activeTab === "New" ||
      c.tier === activeTab;

    return matchesSearch && matchesTab;
  });

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer((prev) =>
      prev && prev.id === customer.id ? null : customer
    );
  };

  return (
    <Navbar pageTitle="Customers">
      <div className="customers-wrapper">
        <div className="customers-container">
          {/* ── LEFT PANEL ── */}
          <div className="customers-left">
            <div className="customers-left-inner">
              {/* Header */}
              <div className="customers-header">
                <div className="customers-header-left">
                  <h2>Customers</h2>
                  <p>{MOCK_CUSTOMERS.length} total customers</p>
                </div>
                <div className="customers-header-actions">
                  <button className="btn-filter">
                    <FunnelIcon style={{ width: 14, height: 14 }} />
                    Filter
                  </button>
                  <button className="btn-download">
                    <ArrowDownTrayIcon style={{ width: 14, height: 14 }} />
                    Download
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="customers-search-wrapper">
                <MagnifyingGlassIcon />
                <input
                  type="text"
                  className="customers-search"
                  placeholder="Search Items here"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              {/* Stat Cards */}
              <div className="customers-stat-cards">
                {SUMMARY_STATS.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className="stat-card">
                      <div className="stat-card-top">
                        <span className="stat-card-label">{stat.label}</span>
                        <div className={`stat-card-icon ${stat.iconClass}`}>
                          <Icon />
                        </div>
                      </div>
                      <div className="stat-card-value">{stat.value}</div>
                    </div>
                  );
                })}
              </div>

              {/* Filter Tabs */}
              <div className="customers-filter-tabs">
                {FILTER_TABS.map((tab) => (
                  <button
                    key={tab}
                    className={`filter-tab${activeTab === tab ? " active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Scrollable Customer List */}
            <div className="customers-list-scroll">
              {filteredCustomers.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: 14,
                    marginTop: 24,
                  }}
                >
                  No customers found.
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <CustomerCard
                    key={customer.id}
                    customer={customer}
                    isSelected={
                      selectedCustomer && selectedCustomer.id === customer.id
                    }
                    onClick={handleSelectCustomer}
                  />
                ))
              )}
            </div>
          </div>

          {/* ── RIGHT PANEL ── */}
          <div className="customers-right">
            {selectedCustomer ? (
              <CustomerDetail
                key={selectedCustomer.id}
                customer={selectedCustomer}
              />
            ) : (
              <EmptyState />
            )}
          </div>
        </div>
      </div>
    </Navbar>
  );
}
